import { useAuthStore } from "@features/authentication/stores";
import { productService } from "@features/shopping/api";
import { useProductStore } from "@features/shopping/stores";
import { handleApiError } from "@shared/utils/error";
import { computed, ref } from "vue";

export function useProducts() {
  // Stores
  const authStore = useAuthStore();
  const productStore = useProductStore();

  // Reactive state
  const searchQuery = ref<string>("");

  // Computed properties from the store
  const countryCode = computed(() => authStore.currentUser?.country_code);
  const products = computed(() => productStore.getProducts);
  const categories = computed(() => productStore.getCategories);
  const loading = computed(() => productStore.loading);
  const error = computed(() => productStore.error);

  /**
   * Filter products based on the search query
   */
  const searchResult = computed(() => {
    if (!productStore.products) {
      return [];
    }

    let result = [...productStore.products];

    // Apply search filter
    if (searchQuery.value.trim() !== "") {
      const searchTerm = searchQuery.value.toLowerCase();
      result = result.filter((product) => product.product_name.toLowerCase().includes(searchTerm));
    }

    return result;
  });

  /**
   * Perform a search
   */
  const search = (query: string) => {
    searchQuery.value = query;
  };

  /**
   * Get products by category
   */
  const getProductsByCategory = async (productCatId: number) => {
    productStore.loading = true;
    productStore.error = null;

    try {
      const response = await productService.getProducts({
        countryCode: countryCode.value ?? undefined,
        memberId: productStore.getMemberId,
        productCatId,
      });

      if (!response.success) {
        productStore.error = response.message;
        return null;
      }

      return response.products;
    } catch (err) {
      productStore.error = handleApiError(err, "Failed to fetch products");
      throw err;
    } finally {
      productStore.loading = false;
    }
  };

  /**
   * Get product details
   */
  const getProductDetails = async (productId: number) => {
    productStore.loading = true;
    productStore.error = null;

    try {
      const response = await productService.getProductDetails(
        productId,
        countryCode.value ?? undefined,
        productStore.getMemberId,
      );

      if (!response.success) {
        productStore.error = response.message;
        return null;
      }

      return response;
    } catch (err) {
      productStore.error = handleApiError(err, "Failed to fetch product details");
      throw err;
    } finally {
      productStore.loading = false;
    }
  };

  /**
   * Clear any error messages
   */
  const clearError = () => productStore.clearError();

  // Methods
  const methods = {
    clearError,
    getProductDetails,
    getProductsByCategory,
    search,
  };

  // States
  const states = {
    categories,
    error,
    loading,
    products,
    searchQuery,
    searchResult,
  };

  return {
    ...methods,
    ...states,
  };
}
